import { useCallback, useRef, useState } from 'react';

const FALLBACK_ERROR = 'Unable to export the quality report. Please try again.';

/** Hands a blob to the browser as a file download. */
export const saveBlobAs = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  try { link.click(); } finally {
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};

// Blob.text() where the runtime has it; FileReader otherwise (jsdom, older Safari).
const readBlobText = (blob: Blob): Promise<string> =>
  typeof blob.text === 'function'
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
      });

/** The backend's `{"error": "..."}` text, else the transport message. */
export const readExportError = async (caught: unknown): Promise<string> => {
  // Axios returns a Blob for error bodies too when responseType is blob.
  const failure = caught as { response?: { data?: unknown }; message?: string };
  let body = failure?.response?.data;
  if (body instanceof Blob) {
    try { body = JSON.parse(await readBlobText(body)); } catch { /* use fallback */ }
  }
  if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string') {
    return body.error;
  }
  return failure?.message || FALLBACK_ERROR;
};

/** Busy/error state around one quality-export download; a second click while
 *  one is in flight is ignored. */
export const useQualityReportDownload = (fetchArchive: () => Promise<Blob>, filename: string) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Guards a double click landing before the busy re-render disables the button.
  const inFlight = useRef(false);

  const download = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      saveBlobAs(await fetchArchive(), filename);
    } catch (caught) {
      setError(await readExportError(caught));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [fetchArchive, filename]);

  return { busy, error, download };
};
